import React, { useContext, useEffect, useMemo, useState } from "react";
import { Confirm, useCreate, useGetIdentity, useNotify, usePermissions, useGetOne, useRefresh } from "react-admin";
import { useParams } from "react-router-dom";

import ReplayIcon from "@mui/icons-material/Replay";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import CircularProgress from "@mui/material/CircularProgress";
import { AiFillWarning } from "react-icons/ai";
import Skeleton from '@mui/material/Skeleton';

import DeleteFileConfirmBtn from "./DeleteFileConfirmBtn";

import { MoveDocFileContext } from "../Ats/ApplicantDetailEdit";
import { ApplicantContext } from "../DocumentAnalysis";
import { useDocumentMove, DOCUMENT_DRAG_MIME } from "../DocumentAnalysis/documentMoveContext";
import { useQuickLook } from "./QuickLook/quickLookContext";
import { DocumentMoveSectionKey, MovableDocument } from "../DocumentAnalysis/documentMoveValidation";
import { checkModuleAccess, isSuperAdmin } from "../../Functions/permissions";
import ssn_masked_placeholder from "../../assets/images/ssn_masked_placeholder.png";
import { typesofUsers } from "../../config";
import moment from "moment";

const NoConfirmIcon = () => null;

interface FilePreviewProps {
  img: string;
  is_Duplicate?: number;
  alt?: string;
  doc_id: string;
  createdAt?: string | null;
  source_name?: string;
  file_name?: string;
  isId?: boolean;
  tag?: string;
  invalidateQueries?: string;
  file?: string;
  /** @deprecated No longer used internally; kept for caller compatibility. */
  isDocUpload?: boolean;
  handleClick?: () => void;
  /** @deprecated No longer used internally; kept for caller compatibility. */
  handledelete?: () => void;
  /** @deprecated No longer used internally; kept for caller compatibility. */
  petIndex?: number;
  /** @deprecated No longer used internally; kept for caller compatibility. */
  handleOnChange?: () => void;
  source_index?: number;
  sourceId?: string | number | null;
  sourceSlot?: "front" | "back" | null;
  documentType?: string | number | null;
  isOfferLetter?: boolean;
  showReprocess?: boolean;
  isDeleted?: boolean;
  props?: any;
  onReprocess?: (docId: string) => void;
}

const FilePreview: React.FC<FilePreviewProps> = ({
  img,
  is_Duplicate = 0,
  alt = "",
  doc_id,
  createdAt = null,
  source_name = "",
  file_name = "",
  isId = false,
  tag,
  invalidateQueries,
  file = img,
  handleClick,
  source_index,
  showReprocess = true,
  isOfferLetter = false,
  isDeleted = false,
  props,
  sourceId = null,
  sourceSlot = null,
  documentType = null,
  onReprocess = (doc_id) => { },
}) => {
  
  const [isProcessing, setisProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [confirmOfferLetterReprocess, setConfirmOfferLetterReprocess] = useState(false);

  // Add this line to fetch the document process status
  const { data: docProcessData } = useGetOne(
    "admin/reprocess-document/isDocumentInProcess",
    { id: doc_id }
  );

  useEffect(() => {
    if (docProcessData?.isDocumentInProcess) {
      setisProcessing(true);
    }else
      setisProcessing(false);
  }, [docProcessData?.isDocumentInProcess]);

  const Identity = useGetIdentity();
  const moveFileContext = useContext(MoveDocFileContext);
  const applicant = useContext(ApplicantContext);
  const documentMove = useDocumentMove();
  const { roletype } = useParams();
  const IsApplicant =
    roletype === "docupload_applicant" ||
    Identity?.data?.userrole === "applicant";

  const isSuperAdminUser = Identity?.data?.userrole === typesofUsers.docuverusSuperAdmin ? true : false;
  const canUseBulkDocumentActions = documentMove.bulkActionsEnabled && isSuperAdminUser && !IsApplicant;
  const documentId = String(doc_id);
  const isCurrentDocMovePending =
    !!moveFileContext?.pendingMoveDocIds?.[documentId] ||
    documentMove.pendingMoveIds.has(documentId);
  // A reprocess request makes the document immutable. Keep this local state in
  // addition to the server status so actions are locked immediately after the
  // request succeeds, before the status query has refreshed.
  const isDocumentLocked = isProcessing || Boolean(docProcessData?.isDocumentInProcess);
  const movableDocument: MovableDocument = useMemo(() => {
    let sectionKey: DocumentMoveSectionKey = "other";
    if (isId) sectionKey = "id";
    else if (source_name === "ssn") sectionKey = "ssn";
    else if (source_name === "paystub") sectionKey = "paystub";
    else if (source_name === "bank") sectionKey = "bank";
    else if (source_name === "other_income") sectionKey = "other_income";

    return {
      id: String(doc_id),
      source: {
        sectionKey,
        sourceId: sourceId ?? props?.sourceId ?? null,
        sourceIndex: source_index ?? props?.source_index,
        slot: sourceSlot ?? null,
      },
      documentType,
      isOfferLetter: Boolean(isOfferLetter || /offer\s*letter/i.test(file_name || "")),
      label: file_name || String(doc_id),
    };
  }, [doc_id, documentType, file_name, isId, isOfferLetter, props?.sourceId, props?.source_index, sourceId, sourceSlot, source_index, source_name]);
  const isSelectedForMove = documentMove.selectedIds.has(String(doc_id));
  const { setDocumentLocked } = documentMove;

  // A document can have been selected just before its reprocess status arrives.
  // Remove that stale selection so it cannot be included in a bulk move/delete.
  useEffect(() => {
    if (isDocumentLocked && isSelectedForMove) {
      documentMove.toggleDocumentSelection(movableDocument);
    }
  }, [documentMove, isDocumentLocked, isSelectedForMove, movableDocument]);

  useEffect(() => {
    setDocumentLocked(documentId, isDocumentLocked);
  }, [documentId, isDocumentLocked, setDocumentLocked]);

  useEffect(
    () => () => setDocumentLocked(documentId, false),
    [documentId, setDocumentLocked]
  );

  const { permissions } = usePermissions();
  const { data: getUserData } = useGetIdentity();

  const permissionToViewSSN = IsApplicant ? true : isSuperAdmin(
    checkModuleAccess(permissions, "View Docs", "View Proof of SSN"),
    getUserData?.userrole
  );

  const appendTokenToUrl = (url: string): string => {
    if (!url) return "";
    const token = Identity?.identity?.tokendata;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}token=${token}`;
  };

  const getShortFileName = (): string => {
    if (file_name) return file_name.substring(0, 9) + "...";
    if (img) {
      const name = decodeURIComponent(img.split("/").pop() || "");
      return name.substring(0, 15) + "...";
    }
    return "";
  };
  const refresh = useRefresh();
  const [create] = useCreate();
  const notify = useNotify();

  const imageUrl = appendTokenToUrl(img);
  const isSSNDoc = source_name === "ssn" || documentType == 4 || tag === "delete_document_ssn_document";
  const isSsnMasked = !permissionToViewSSN && isSSNDoc;
  const imageUrlAsPerPermission = isSsnMasked ? ssn_masked_placeholder : imageUrl;

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [imageUrlAsPerPermission]);

  const quickLook = useQuickLook();
  const quickLookUrl = isSsnMasked
    ? ssn_masked_placeholder
    : appendTokenToUrl(file);
  useEffect(() => {
    if (!doc_id) return;
    const id = String(doc_id);
    quickLook.register(id, {
      fileUrl: quickLookUrl,
      fileName: file_name,
      isMasked: isSsnMasked,
      sectionKey: movableDocument.source.sectionKey,
      uploadedAt: createdAt,
      isDuplicate: is_Duplicate,
      sourceId: movableDocument.source.sourceId,
    });
    return () => quickLook.unregister(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    doc_id,
    quickLookUrl,
    file_name,
    isSsnMasked,
    createdAt,
    is_Duplicate,
    movableDocument.source.sectionKey,
    movableDocument.source.sourceId,
  ]);
  const parseTimestamp = (value?: string | null): number | null => {
    if (!value) return null;
    const strictIso = moment(value, moment.ISO_8601, true);
    if (strictIso.isValid()) return strictIso.valueOf();
    const strictCommon = moment(
      value,
      [
        "YYYY-MM-DD HH:mm:ss",
        "YYYY-MM-DD HH:mm:ss.SSS",
        "MM/DD/YYYY HH:mm:ss",
        "MM/DD/YYYY h:mm A",
      ],
      true
    );
    if (strictCommon.isValid()) return strictCommon.valueOf();
    const nativeTime = new Date(value).getTime();
    return Number.isNaN(nativeTime) ? null : nativeTime;
  };
  const applicationStatusUpdatedOn =
    applicant?.applicantInfo?.applicationStatusUpdatedOn;
  const hasDuplicateBadge = is_Duplicate === 1;
  const canShowNewDocumentBadge = isSuperAdminUser && !IsApplicant;
  const isNewDocument = useMemo(() => {
    const createdTime = parseTimestamp(createdAt);
    if (createdTime === null) return false;
    if (!applicationStatusUpdatedOn) return true;
    const statusUpdatedTime = parseTimestamp(applicationStatusUpdatedOn);
    if (statusUpdatedTime === null) return true;
    return createdTime > statusUpdatedTime;
  }, [createdAt, applicationStatusUpdatedOn]);

  const startReprocess = (convertOfferLetterToPaystub = false) => {
    const data = convertOfferLetterToPaystub
      ? { app_doc_id: doc_id, convertOfferLetterToPaystub: true }
      : { app_doc_id: doc_id };

    create(
      "admin/reprocess-document",
      { data },
      {
        onSuccess: () => {
          setConfirmOfferLetterReprocess(false);
          notify("Document reprocessing initiated successfully", {
            type: "success",
            autoHideDuration: 5000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
          onReprocess(doc_id);
          setisProcessing(true);
          refresh();
        },
        onError: (error: any) => {
          setConfirmOfferLetterReprocess(false);
          const message =
            error?.body?.error ||
            error?.body?.message ||
            error?.error ||
            error?.message ||
            "Document reprocessing failed";
          notify(message, {
            type: "error",
            autoHideDuration: 5000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
          setisProcessing(false);
          refresh();
        },
      }
    );
  };

  const handleReprocess = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (isDocumentLocked || isCurrentDocMovePending) return;
    if (isOfferLetter) {
      setConfirmOfferLetterReprocess(true);
      return;
    }
    startReprocess();
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const isQuickLookFocused =
    quickLook.isAvailable && quickLook.focusedThumbnailId === String(doc_id);

  const handlePreviewClick = () => {
    if (isCurrentDocMovePending) return;
    handleClick?.();
    if (quickLook.isAvailable && doc_id) {
      const id = String(doc_id);
      if (quickLook.focusedThumbnailId === id) {
        quickLook.clearFocusedEntry(id);
      } else {
        quickLook.focusEntry(id, {
          fileUrl: quickLookUrl,
          fileName: file_name,
          isMasked: isSsnMasked,
          sectionKey: movableDocument.source.sectionKey,
          uploadedAt: createdAt,
          isDuplicate: is_Duplicate,
          sourceId: movableDocument.source.sourceId,
        });
      }
    }
  };

  const handleSelectDocument = (event?: React.MouseEvent | React.KeyboardEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!canUseBulkDocumentActions) return;
    if (isCurrentDocMovePending || isDocumentLocked || !doc_id) return;
    documentMove.toggleDocumentSelection(movableDocument);
    // Blur so spacebar doesn't re-toggle the checkbox.
    const target = event?.currentTarget;
    if (target instanceof HTMLElement) target.blur();
  };

  const setDocumentDragImage = (event: React.DragEvent<HTMLDivElement>, count: number) => {
    const sourceImg = event.currentTarget.querySelector("img");
    if (!sourceImg) return;
    const ghost = document.createElement("div");
    Object.assign(ghost.style, {
      position: "fixed",
      top: "-1000px",
      left: "-1000px",
      zIndex: "9999",
    });
    const imgClone = sourceImg.cloneNode(true) as HTMLImageElement;
    imgClone.removeAttribute("data-testid");
    imgClone.setAttribute("aria-hidden", "true");
    Object.assign(imgClone.style, {
      width: "72px",
      height: "92px",
      objectFit: "cover",
      borderRadius: "6px",
      border: "1px solid #cfd4dc",
      background: "#ffffff",
      display: "block",
    });
    ghost.appendChild(imgClone);

    if (count > 1) {
      const badge = document.createElement("span");
      badge.textContent = String(count);
      Object.assign(badge.style, {
        position: "absolute",
        top: "-7px",
        right: "-7px",
        minWidth: "20px",
        height: "20px",
        padding: "0 5px",
        borderRadius: "999px",
        background: "#286df0",
        color: "#ffffff",
        fontSize: "11px",
        fontWeight: "700",
        lineHeight: "20px",
        textAlign: "center",
        boxShadow: "0 1px 3px rgba(31, 54, 85, 0.3)",
      });
      ghost.appendChild(badge);
    }

    document.body.appendChild(ghost);
    event.dataTransfer.setDragImage(ghost, 36, 46);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    if (!canUseBulkDocumentActions || isDocumentLocked || isCurrentDocMovePending || !doc_id) {
      event.preventDefault();
      return;
    }
    if (!isSelectedForMove) {
      documentMove.setDragSelectedIds([String(doc_id)]);
      event.dataTransfer.setData("text/plain", String(doc_id));
      event.dataTransfer.setData(DOCUMENT_DRAG_MIME, String(doc_id));
      setDocumentDragImage(event, 1);
      return;
    }
    const ids = documentMove.selectedDocuments.map((document) => document.id);
    documentMove.setDragSelectedIds(ids);
    event.dataTransfer.setData("text/plain", ids.join(","));
    event.dataTransfer.setData(DOCUMENT_DRAG_MIME, ids.join(","));
    setDocumentDragImage(event, ids.length);
  };

  const handleDragEnd = () => {
    documentMove.setDragSelectedIds([]);
  };

  return (
    <>
      <Confirm
        isOpen={confirmOfferLetterReprocess}
        sx={{
          "& .RaConfirm-iconPaddingStyle": {
            display: "none",
          },
          "& .MuiButton-startIcon": {
            display: "none",
          },
          "& .MuiDialogActions-root": {
            padding: "8px 24px 16px",
            gap: 1,
          },
          "& .MuiDialogActions-root .ra-confirm": {
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            padding: "6px 16px",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          },
        }}
        ConfirmIcon={NoConfirmIcon}
        CancelIcon={NoConfirmIcon}
        title="Offer Letter Cannot Be Reprocessed"
        content="Offer letters cannot be directly reprocessed. Do you want to change this document to a pay stub and start reprocessing?"
        confirm="Change to pay stub"
        cancel="Leave as Offer Letter"
        onConfirm={() => startReprocess(true)}
        onClose={() => setConfirmOfferLetterReprocess(false)}
      />
      {img && (
        <div className={`d-inline-flex flex-column flex-wrap${canUseBulkDocumentActions && !isDocumentLocked ? " document-preview-selectable" : ""} ${isSelectedForMove ? "selected" : ""}`} draggable={canUseBulkDocumentActions && !isDocumentLocked && !isCurrentDocMovePending} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div
            style={{
              width: "80px",
              height: "18px",
              margin: "0 auto 2px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "2px",
            }}
          >
            {isCurrentDocMovePending ? (
              <span
                data-testid="moving-indicator"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "10px",
                  color: "#666",
                  lineHeight: "12px",
                }}
              >
                <CircularProgress size={10} thickness={6} />
                <span>moving</span>
              </span>
            ) : canShowNewDocumentBadge && isNewDocument ? (
              <span
                style={{
                  backgroundColor: "#01DB3E",
                  color: "#fff",
                  fontSize: "10px",
                  fontWeight: 700,
                  padding: "1px 6px",
                  borderRadius: "10px",
                  lineHeight: "12px",
                  pointerEvents: "none",
                }}
              >
                NEW
              </span>
            ) : null}
            {!isCurrentDocMovePending && hasDuplicateBadge ? (
              <span
                style={{
                  fontSize: "11px",
                  lineHeight: "14px",
                  color: "#f39c12",
                  whiteSpace: "nowrap",
                  background: "rgba(255, 255, 255, 0.9)",
                  borderRadius: "8px",
                  padding: "0 4px",
                  pointerEvents: "none",
                }}
              >
                <AiFillWarning style={{ verticalAlign: "text-bottom" }} /> Duplicate
              </span>
            ) : null}
          </div>
          <div style={{ position: "relative", display: "inline-block" }}>
            {showReprocess && isSuperAdminUser && !isLoading && !IsApplicant && (
              <button
                onClick={handleReprocess}
                className="print-hide"
                aria-label="Reprocess document"
                data-testid="reprocess-doc-btn"
                disabled={isCurrentDocMovePending || isDocumentLocked}
                style={{
                  position: "absolute",
                  top: "4px",
                  left: "4px",
                  background: isProcessing ? "#d61324" : "#007bff",
                  border: "none",
                  borderRadius: "50%",
                  padding: "4px",
                  cursor: isCurrentDocMovePending || isDocumentLocked ? "not-allowed" : "pointer",
                  zIndex: 2,
                  opacity: isCurrentDocMovePending || isDocumentLocked ? 0.6 : 1,
                }}
                title={isDocumentLocked ? "Document is reprocessing" : "Reprocess"}
              >
                <ReplayIcon style={{ color: "#fff", fontSize: 16 }} />
              </button>
            )}

            {isLoading && !docProcessData?.isDocumentInProcess && (
              <Skeleton
                variant="rectangular"
                width={80}
                height={100}
                animation="wave"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 1
                }}
              />
            )}

            {hasError ? (
              <div
                role="img"
                aria-label={alt || "Document preview unavailable"}
                onClick={handlePreviewClick}
                data-testid="document-preview-error"
                style={{
                  width: "80px",
                  height: "100px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  cursor: isCurrentDocMovePending ? "not-allowed" : "pointer",
                  outline: isQuickLookFocused ? "2px solid #1976d2" : undefined,
                  outlineOffset: isQuickLookFocused ? "2px" : undefined,
                }}
              >
                <AiFillWarning color="#dc3545" size={24} aria-hidden="true" />
              </div>
            ) : (
              <img
                src={imageUrlAsPerPermission}
                draggable={false}
                style={{
                  maxWidth: "80px",
                  cursor: isCurrentDocMovePending ? "not-allowed" : "pointer",
                  opacity: isLoading ? 0 : 1,
                  transition: 'opacity 0.3s ease-in-out',
                  outline: isQuickLookFocused ? "2px solid #1976d2" : undefined,
                  outlineOffset: isQuickLookFocused ? "2px" : undefined,
                }}
                className="img-thumbnail ratio ratio-1x1"
                alt={alt || "Document preview"}
                onClick={handlePreviewClick}
                loading="eager"
                data-testid="document-preview-image"
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            )}
          </div>

          {!isLoading && !isDocumentLocked && <div style={{ textAlign: "center" }}>
            <p
              className="small pt-1"
              title={file_name}
              style={{ fontSize: 10 }}
            >
              {getShortFileName()}
            </p>
            {canUseBulkDocumentActions ? (
              <button
                type="button"
                className={`document-preview-select-toggle print-hide ${isSelectedForMove ? "selected" : ""}`}
                onClick={handleSelectDocument}
                aria-label={`${isSelectedForMove ? "Clear selection for" : "Select"} document ${file_name || doc_id}`}
                aria-pressed={isSelectedForMove}
                disabled={isCurrentDocMovePending || isDocumentLocked}
              >
                <span className="document-preview-checkmark" aria-hidden="true" />
              </button>
            ) : (
              <fieldset
                disabled={isCurrentDocMovePending || isDocumentLocked}
                style={{ border: "none", padding: 0, margin: 0, minInlineSize: "auto" }}
              >
                <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
                  <DeleteFileConfirmBtn
                    doc_id={doc_id}
                    invalidateQueries={invalidateQueries}
                    tag={tag}
                    petIndex={props?.petIndex}
                    handleOnChange={props?.handleOnChange}
                  />
                  {!IsApplicant && (
                    <button
                      type="button"
                      onClick={() =>
                        moveFileContext?.setMoveFileState({
                          isOpen: true,
                          docId: doc_id,
                          trnID: applicant?.applicantInfo?.trnId,
                          applicantID: applicant?.applicantInfo?.applicantId,
                          source_index: props?.source_index,
                          source_name: source_name,
                        })
                      }
                      disabled={isCurrentDocMovePending || isDocumentLocked}
                      className="print-hide"
                      data-testid="move-doc-btn"
                      aria-label="Move document"
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: isCurrentDocMovePending ? "not-allowed" : "pointer",
                        padding: 0,
                        margin: 0,
                        color: isCurrentDocMovePending ? "#aaa" : "#007bff",
                      }}
                      title={isCurrentDocMovePending ? "A move is already in progress for this document" : "Move"}
                    >
                      <DriveFileMoveIcon fontSize="small" />
                    </button>
                  )}
                </div>
              </fieldset>
            )}

          </div>}
        </div>
      )}
    </>
  );
};

export default FilePreview;
